/**
 * Lightweight visit tracker — fires one POST /api/track/visit per browser
 * session and refreshes once per UTC day. Feeds the admin analytics tab.
 *
 * Design notes:
 *  - Anon ID is a random UUID stored in localStorage. No cookies, no PII.
 *  - Backend dedupes by (anon_id, day), so a single pageload + SPA navs +
 *    refreshes all collapse into one "visit" for the day.
 *  - Auth status is sent via apiClient (which attaches the session cookie
 *    or Bearer token automatically), so logged-in users get attributed.
 */
import { useEffect } from "react";
import { apiClient } from "@/lib/api";

const ANON_KEY = "pd_anon_id";
const DAY_KEY = "pd_visit_day";

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older Safari versions
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function _todayUTC() {
  return new Date().toISOString().slice(0, 10);
}

export function getAnonId() {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = _uuid();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function useVisitTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const today = _todayUTC();
    let lastSent;
    try {
      lastSent = localStorage.getItem(DAY_KEY);
    } catch {
      lastSent = null;
    }
    if (lastSent === today) return; // already counted today
    const anon = getAnonId();
    if (!anon) return;
    apiClient
      .post("/track/visit", {
        anon_id: anon,
        path: window.location.pathname,
        referrer: document.referrer || null,
      })
      .then(() => {
        try {
          localStorage.setItem(DAY_KEY, today);
        } catch {
          // Quota errors / private mode — silently skip, we'll retry next visit.
        }
      })
      .catch(() => {
        // Network error — silently skip; not worth retrying or surfacing.
      });
  }, []);
}
